/**
 * Data-residency regions supported by Physiogenic.
 *
 * `code`       — persisted to Mongo as `clinics.data_residency`. NEVER
 *                 renumber or reuse an existing code — clinics rely on it
 *                 for legal binding after signup.
 * `region`     — MongoDB Atlas region label (informational; shown to admin).
 * `country`    — ISO-3166 alpha-2 country code (informational).
 * `short`      — badge label ("🇪🇺 Data in EU").
 * `full`       — long label for tooltips + option lists.
 * `regulation` — headline law relevant to healthcare data in that region
 *                 (shown as a subtitle so clinic owners understand the
 *                 legal weight of their choice).
 * `available`  — false = shown as coming-soon / disabled. Keep here so
 *                 the marketing surface can still enumerate.
 *
 * Iter 122 — expanded from 4 → 12 regions covering the major regulated
 * healthcare markets (EU/UK/CH · US East + West · CA · BR · AU · SG · JP · AE · IN).
 */
export const RESIDENCY_REGIONS = [
  { code: 'eu', region: 'eu-central-1',  country: 'DE', short: '🇪🇺 Data in EU',    full: 'European Union (Frankfurt)',   regulation: 'GDPR · EHDS · MDR',       available: true, default: true },
  { code: 'uk', region: 'eu-west-2',     country: 'GB', short: '🇬🇧 Data in UK',    full: 'United Kingdom (London)',        regulation: 'UK GDPR · NHS DSPT',      available: true },
  { code: 'ch', region: 'eu-central-2',  country: 'CH', short: '🇨🇭 Data in CH',   full: 'Switzerland (Zurich)',            regulation: 'nFADP · KVG · MedBG',     available: true },
  { code: 'us', region: 'us-east-1',     country: 'US', short: '🇺🇸 Data in US-E', full: 'United States (N. Virginia)',    regulation: 'HIPAA · HITECH · state PHI', available: true },
  { code: 'usw', region: 'us-west-2',    country: 'US', short: '🇺🇸 Data in US-W', full: 'United States (Oregon)',         regulation: 'HIPAA · CCPA · CMIA',     available: true },
  { code: 'ca', region: 'ca-central-1',  country: 'CA', short: '🇨🇦 Data in CA',   full: 'Canada (Toronto)',                regulation: 'PIPEDA · PHIPA · Q. Law 25', available: true },
  { code: 'br', region: 'sa-east-1',     country: 'BR', short: '🇧🇷 Data in BR',   full: 'Brazil (São Paulo)',              regulation: 'LGPD · CFF resolutions',   available: true },
  { code: 'ap', region: 'ap-southeast-2',country: 'AU', short: '🇦🇺 Data in AU',   full: 'Australia (Sydney)',              regulation: 'Privacy Act · APPs · My Health Records', available: true },
  { code: 'sg', region: 'ap-southeast-1',country: 'SG', short: '🇸🇬 Data in SG',   full: 'Singapore',                        regulation: 'PDPA · MOH LG',            available: true },
  { code: 'jp', region: 'ap-northeast-1',country: 'JP', short: '🇯🇵 Data in JP',   full: 'Japan (Tokyo)',                    regulation: 'APPI · Medical Care Act',  available: true },
  { code: 'ae', region: 'me-central-1',  country: 'AE', short: '🇦🇪 Data in AE',   full: 'UAE (Dubai)',                       regulation: 'DHA · DoH · Federal PDPL',  available: true },
  { code: 'in', region: 'ap-south-1',    country: 'IN', short: '🇮🇳 Data in IN',   full: 'India (Mumbai)',                    regulation: 'DPDPA 2023 · Telemedicine Act', available: false },
];

/** Lookup helpers — return a sensible fallback if the code is unknown. */
const _byCode = Object.fromEntries(RESIDENCY_REGIONS.map(r => [r.code, r]));

export const RESIDENCY_LABELS = Object.fromEntries(
  RESIDENCY_REGIONS.map(r => [r.code, r.short]),
);
export const RESIDENCY_FULL = Object.fromEntries(
  RESIDENCY_REGIONS.map(r => [r.code, r.full]),
);

export function getResidency(code) {
  return _byCode[(code || 'eu').toLowerCase()] || _byCode.eu;
}
